import { useState, useEffect, useRef } from 'react';
import { AddressExposurePanel, BreachExposurePanel, CompaniesHousePanel, Dashboard, HelpTip, HowItWorks, HostileMentionPanel, IncidentGroups, IntelPacketPanel, KnownAssociatesPanel, LinkedBusinessesPanel, LiveLocationPanel, Loading, OverviewTab, PANEL_HELP, PersistentPostersPanel, PiplLookupPanel, OsintLookupPanel, ProlificClustersPanel, RATINGS, ReorderablePanels, ReportsPanel, SubjectBrief, SubscriptionPanel, TelegramChannelsPanel, TimelinePanel, WatchStatusStrip, api } from './_barrel.js';


        function SubjectDetailView({ token, subjectId, setView, openDialog, setSelectedBusiness, me }) {
            const [subject, setSubject] = useState(null);
            const [incidents, setIncidents] = useState(null);
            const [assessments, setAssessments] = useState(null);
            const [reports, setReports] = useState(null);
            const [rubric, setRubric] = useState(null);
            const [timeline, setTimeline] = useState(null);
            const [associates, setAssociates] = useState(null);
            const [addressExposure, setAddressExposure] = useState(null);
            const [incidentsOpen, setIncidentsOpen] = useState(false);  // record opens compact
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [tab, setTab] = useState('overview');  // 'overview' | 'brief' | 'details'

            useEffect(() => {
                (async () => {
                    try {
                        const [s, i, a, r, rub, tl, assoc, addrs] = await Promise.all([
                            api(`/api/v1/subjects/${subjectId}`, {}, token),
                            api(`/api/v1/incidents?subject_id=${subjectId}&limit=200`, {}, token),
                            api(`/api/v1/assessments?subject_id=${subjectId}&limit=10`, {}, token),
                            api(`/api/v1/subjects/${subjectId}/reports`, {}, token).catch(() => []),
                            api(`/api/v1/assessments/rubric?subject_id=${subjectId}&prefer=signed`, {}, token).catch(() => null),
                            api(`/api/v1/subjects/${subjectId}/timeline`, {}, token).catch(() => null),
                            api(`/api/v1/subjects/${subjectId}/associates`, {}, token).catch(() => null),
                            api(`/api/v1/subjects/${subjectId}/address-exposure`, {}, token).catch(() => null),
                        ]);
                        setSubject(s); setIncidents(i); setAssessments(a); setReports(r); setRubric(rub); setTimeline(tl); setAssociates(assoc); setAddressExposure(addrs);
                    } catch (e) { setError(e.message); }
                })();
            }, [token, subjectId, refresh]);

            // Shared filter: clicking a threat actor in Watched Accounts sets
            // this, and Hostile Mention Watch narrows to that account's posts.
            // Declared before ANY early return so hook order is stable (React #310).
            const [selectedAccount, setSelectedAccount] = useState(null);
            const hostilePanelRef = useRef(null);
            const focusAccount = (acct) => {
                setSelectedAccount(acct);
                setTimeout(() => {
                    if (hostilePanelRef.current) {
                        hostilePanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 50);
            };
            // Early returns come only AFTER every hook above, so the hook count
            // is identical on every render.
            if (error) return <div className="max-w-5xl mx-auto px-6 py-8 text-[var(--red)]">{error}</div>;
            if (!subject || !incidents || !assessments) return <Loading />;

            const activeAssessment = assessments.items.find(a => !a.superseded_at);
            const reload = () => setRefresh(r => r+1);

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <a className="text-xs font-mono uppercase tracking-widest text-muted cursor-pointer hover:text-[var(--gold)] inline-block mb-6"
                       onClick={() => setView('subjects')}>← Subjects</a>

                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Subject Dossier</div>
                            <h2 className="font-display text-5xl gold mb-2">{subject.display_name || subject.full_name}</h2>
                            <div className="text-muted font-mono text-sm">
                                {subject.full_name} · {subject.subject_type_code}
                                {subject.is_minor && <span className="ml-3" style={{color: 'var(--amber)'}}>· MINOR · RESTRICTED</span>}
                            </div>
                            {subject.client_tag && (
                                <div className="text-sm font-mono mt-2" style={{color:'var(--gold)'}}>
                                    {subject.client_tag}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3 flex-wrap justify-end">
                            <button className="btn-ghost" onClick={async () => {
                                if (!confirm('Run Google News collection for this subject now? (Roughly 1 minute.)')) return;
                                try {
                                    await api('/api/v1/run-now', { method: 'POST',
                                        body: JSON.stringify({ scope: 'subject', target_id: subjectId }) }, token);
                                    alert('Started. Watch progress on the Dashboard.');
                                } catch (e) { alert(`Failed: ${e.message}`); }
                            }}>↻ Collect now</button>
                            <button className="btn-ghost" onClick={() => openDialog('archive-subject', { subject, onSuccess: () => setView('subjects') })}>⌫ Archive</button>
                            {me?.analyst?.role === 'admin' && (
                                <button className="btn-ghost" style={{color:'var(--red)', borderColor:'var(--red)'}} title="GDPR right-to-erasure: permanently delete this subject and all associated records"
                                    onClick={async () => {
                                        const name = subject.full_name || subject.display_name || '';
                                        const typed = prompt(
                                            `PERMANENTLY ERASE "${name}" and ALL associated records ` +
                                            `(aliases, mentions, incidents, assessments, reports, trips, subscriptions…)?\n\n` +
                                            `This is GDPR right-to-erasure — unlike Archive, NOTHING is retained and it CANNOT be undone.\n\n` +
                                            `Type the subject's full name to confirm:`);
                                        if (typed === null) return;
                                        if (typed.trim().toLowerCase() !== name.trim().toLowerCase()) {
                                            alert('Name did not match — nothing was erased.'); return;
                                        }
                                        const reason = prompt('Optional: reason for erasure (recorded in the audit log):', '');
                                        try {
                                            const res = await api(`/api/v1/subjects/${subjectId}/erase`, { method: 'POST',
                                                body: JSON.stringify({ confirm_full_name: typed, reason: reason || null }) }, token);
                                            const tables = Object.entries(res.rows_deleted || {}).map(([k,v]) => `  ${k}: ${v}`).join('\n');
                                            alert(`Erased "${name}".\n${res.total_rows} record(s) removed:\n${tables}`);
                                            setView('subjects');
                                        } catch (e) { alert(`Erase failed: ${e.message}`); }
                                    }}>⌦ Erase (GDPR)</button>
                            )}
                            {['admin','lead_analyst','analyst'].includes(me?.analyst?.role) && (
                                <button className="btn-ghost" title="Data-subject access request: download everything held on this subject as JSON"
                                    onClick={async () => {
                                        try {
                                            const res = await api(`/api/v1/subjects/${subjectId}/export`, {}, token);
                                            const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
                                            const url = URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.download = `subject-${subjectId}-export.json`;
                                            document.body.appendChild(link); link.click(); link.remove();
                                            URL.revokeObjectURL(url);
                                            const n = res?.export_metadata?.total_records ?? '?';
                                            alert(`Exported ${n} record(s) for ${subject.full_name}. The JSON file has downloaded.`);
                                        } catch (e) { alert(`Export failed: ${e.message}`); }
                                    }}>⬇ Export data (DSAR)</button>
                            )}
                            <button className="btn-ghost" onClick={() => openDialog('edit-subject', { subject, onSuccess: reload })}>✎ Edit Subject</button>
                            <button className="btn-ghost" onClick={() => openDialog('new-incident', { subjectId, onSuccess: reload })}>+ Log Incident</button>
                            <button className="btn-gold" onClick={() => openDialog(activeAssessment ? 'supersede-assessment' : 'new-assessment', { subjectId, currentAssessment: activeAssessment, onSuccess: reload })}>
                                {activeAssessment ? 'Update Assessment' : 'Run Assessment'}
                            </button>
                        </div>
                    </div>

                    <HowItWorks id="subject_dossier" title="How this dossier works"
                        intro="Everything held on this subject, organised into three tabs — start at Overview, read the Brief, drill into Details."
                        steps={[
                            "<b>Overview</b> — the at-a-glance risk picture and headline signals.",
                            "<b>Brief</b> — the written assessment and one-page summary you can issue.",
                            "<b>Details</b> — every panel: hostile mentions, breaches, corporate links, timeline and more. Each panel has a <b>?</b> explaining it; collapse the ones you don't need.",
                            "Actions are top-right: run collection, log an incident, run/update the assessment, or generate a report.",
                        ]} />

                    {/* Monitoring panel — shown above the tab toggle so it's
                        visible from both Brief and Details views */}
                    <SubscriptionPanel token={token} targetKind="subject" targetId={subjectId} />

                    <ProlificClustersPanel token={token} subject={subject} reload={reload} onSelectAccount={focusAccount} />

                    <PersistentPostersPanel subject={subject} onSelectAccount={focusAccount} />

                    <div className="flex gap-1 mb-6 border-b border-veil">
                        <button
                            onClick={() => setTab('overview')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{
                                color: tab === 'overview' ? 'var(--gold)' : 'var(--muted)',
                                borderBottom: tab === 'overview' ? '2px solid var(--gold)' : '2px solid transparent',
                                marginBottom: '-1px',
                            }}>
                            Overview
                        </button>
                        <button
                            onClick={() => setTab('brief')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{
                                color: tab === 'brief' ? 'var(--gold)' : 'var(--muted)',
                                borderBottom: tab === 'brief' ? '2px solid var(--gold)' : '2px solid transparent',
                                marginBottom: '-1px',
                            }}>
                            Brief
                        </button>
                        <button
                            onClick={() => setTab('details')}
                            className="px-5 py-3 font-mono text-xs uppercase tracking-widest"
                            style={{
                                color: tab === 'details' ? 'var(--gold)' : 'var(--muted)',
                                borderBottom: tab === 'details' ? '2px solid var(--gold)' : '2px solid transparent',
                                marginBottom: '-1px',
                            }}>
                            Details
                        </button>
                    </div>

                    {tab === 'overview' && (
                        <OverviewTab subject={subject} token={token}
                                     incidents={incidents} assessments={assessments} rubric={rubric}
                                     onGoToDetails={() => setTab('details')} />
                    )}
                    {tab === 'brief' && (
                        <SubjectBrief
                            subject={subject}
                            incidents={incidents}
                            assessments={assessments}
                            reports={reports}
                            token={token}
                            rubric={rubric}
                            onGoToDetails={() => setTab('details')}
                            onGenerateReport={() => setTab('details')}
                        />
                    )}

                    {tab === 'details' && (
                    <>
                    <WatchStatusStrip incidents={incidents} assessments={assessments} subject={subject} token={token} subjectId={subjectId} rubric={rubric} />

                    <div className="grid grid-cols-3 gap-6 mb-10">
                        <div className="panel p-6 col-span-1">
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-4">Processing Basis</div>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Lawful Basis</div>
                                    <div>{subject.lawful_basis.replace('_',' ')}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Purpose</div>
                                    <div>{subject.processing_purpose}</div>
                                </div>
                                {subject.lia_reference && (
                                    <div>
                                        <div className="text-xs text-muted uppercase tracking-widest">LIA Reference</div>
                                        <div className="font-mono text-xs">{subject.lia_reference}</div>
                                    </div>
                                )}
                                {subject.dpia_reference && (
                                    <div>
                                        <div className="text-xs text-muted uppercase tracking-widest">DPIA Reference</div>
                                        <div className="font-mono text-xs">{subject.dpia_reference}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="panel col-span-2">
                            <div className="panel-header">
                                <div className="font-display text-xl gold">Risk Score<HelpTip text={PANEL_HELP.risk_score} /></div>
                                <div className="font-mono text-xs text-muted mt-1">
                                    His signed risk band, and the signals the platform computes from right now.
                                </div>
                            </div>
                            {rubric ? (
                                <div className="px-6 py-6">
                                    {(() => {
                                        // Single source of truth = the rubric score the analyst signs
                                        // (rubric.composite is the effective value: analyst override if
                                        // set, else the computed signals score). When signed, it is locked
                                        // and labelled analyst-signed; the tile, dashboard and report all
                                        // read this same number.
                                        const headline = Number(rubric.composite);
                                        const isSigned = !!rubric.signed_at;
                                        // What's pushing the score up: the elevated signals, worst first.
                                        const drivers = (rubric.inputs || [])
                                            .filter(s => Number(s.score) >= 3)
                                            .sort((a, b) => (b.score * b.weight) - (a.score * a.weight))
                                            .slice(0, 2)
                                            .map(s => `${s.label} (${s.score}/5${s.reason ? ' — ' + s.reason : ''})`);
                                        return (
                                        <>
                                        <div className="flex items-end gap-5 mb-2">
                                            <div className="font-display" style={{fontSize:'4.5rem', lineHeight:0.9, color: RATINGS.hex(headline)}}>{headline.toFixed(1)}</div>
                                            <div className="pb-2">
                                                <div className="font-mono text-base uppercase tracking-widest" style={{ color: RATINGS.hex(headline) }}>{RATINGS.label(headline)}</div>
                                                <div className="font-mono text-xs text-muted mt-1">out of 5.0 · {isSigned ? 'analyst-signed' : 'unsigned — sign to lock'}</div>
                                                {rubric.is_override && (
                                                    <div className="font-mono text-xs mt-1" style={{color:'var(--amber)'}}>analyst override · system computed {Number(rubric.rubric_composite).toFixed(1)}</div>
                                                )}
                                                {drivers.length > 0 && (
                                                    <div className="font-mono text-xs mt-1 text-muted">driven mainly by {drivers.join(' · ')}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="font-mono text-xs text-muted mb-4">
                                            Bands: Low &lt;2.5 · Moderate 2.5–3.4 · High 3.5–4.4 · Critical ≥4.5
                                        </div>
                                        <div className="text-xs text-muted mb-6 pb-5 border-b border-[var(--line)]" style={{lineHeight:1.65}}>
                                            <b className="text-paper">What this means.</b> The big figure is his <b>risk band</b> — the one authoritative number, shown the same here, on the dashboard and in every report, on the Low / Moderate / High / Critical scale. It is the weighted average of the <b>signals below</b>. When an analyst signs it, it&rsquo;s <b>locked</b> as the record; re-running the sources recomputes it, and signing again adopts the new number. An analyst may override the score with a reason where their judgement differs.
                                        </div>
                                        </>
                                        );
                                    })()}

                                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-4">Contributing signals</div>
                                    <table className="w-full" style={{borderCollapse:'collapse'}}>
                                        <thead>
                                            <tr className="font-mono text-xs text-muted uppercase tracking-widest">
                                                <th className="text-left font-normal pb-2">Signal</th>
                                                <th className="text-center font-normal pb-2 px-3">Score</th>
                                                <th className="text-center font-normal pb-2 px-3">Weight</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rubric.inputs.map((inp) => (
                                                <tr key={inp.name} className="border-t border-[var(--line)]">
                                                    <td className="py-3 pr-4">
                                                        <div className="text-paper">{inp.label}</div>
                                                        <div className="font-mono text-xs text-muted mt-0.5">{inp.reason}</div>
                                                    </td>
                                                    <td className="py-3 px-3 text-center font-mono">
                                                        {inp.score == null
                                                            ? <span className="text-muted text-base" title="Not assessed — no collection run recorded for this signal">—</span>
                                                            : <><span className="gold text-base">{inp.score}</span><span className="text-muted text-xs">/5</span></>}
                                                    </td>
                                                    <td className="py-3 px-3 text-center font-mono text-muted text-sm">×{inp.weight.toFixed(1)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="font-mono text-xs text-muted mt-4">
                                        Composite = weighted average of {rubric.inputs.length} signals (total weight {Number(rubric.total_weight).toFixed(1)}) = <span className="gold">{Number(rubric.composite).toFixed(1)}</span>
                                    </div>

                                    {rubric.signed_at ? (
                                        <div className="mt-6 pt-5 border-t border-[var(--line)]">
                                            <div className="font-mono text-xs uppercase tracking-widest gold mb-2">Analyst Sign-Off</div>
                                            <div className="font-mono text-xs text-muted mb-3">
                                                Signed off by <span className="text-paper">{rubric.signed_by_display_name}</span> on {new Date(rubric.signed_at).toLocaleString('en-GB')}
                                            </div>
                                            {rubric.assessment_summary && (
                                                <div className="text-sm leading-relaxed whitespace-pre-line text-paper mb-3">{rubric.assessment_summary}</div>
                                            )}
                                            {rubric.recommendations && (
                                                <div className="mt-2">
                                                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">Recommendations</div>
                                                    <div className="text-sm leading-relaxed whitespace-pre-line text-muted">{rubric.recommendations}</div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="mt-6 pt-5 border-t border-[var(--line)] font-mono text-xs text-muted">
                                            Not yet signed off. Use "Run Assessment" to review and sign.
                                        </div>
                                    )}

                                    <details className="mt-5 pt-4 border-t border-[var(--line)]">
                                        <summary className="font-mono text-xs uppercase tracking-widest text-muted cursor-pointer hover:text-[var(--gold)]">How the score works (methodology)</summary>
                                        <div className="text-sm text-muted mt-3 whitespace-pre-line leading-relaxed">{rubric.methodology}</div>
                                    </details>
                                </div>
                            ) : (
                                <div className="px-6 py-12 text-center">
                                    <div className="text-muted">No risk score yet. Generate a report to compute one.</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Analyst-reorderable panel stack — drag any panel by its grip
                        (top-right, on hover) to reorder; the layout persists per analyst. */}
                    <ReorderablePanels storageKey="veil_panelorder_subject_details" items={[
                        { id: 'timeline', node: <TimelinePanel timeline={timeline} /> },
                        { id: 'associates', node: <KnownAssociatesPanel associates={associates} /> },
                        { id: 'address', node: <AddressExposurePanel addresses={addressExposure} /> },
                        { id: 'reports', node: <ReportsPanel token={token} subjectId={subjectId} subject={subject} /> },
                        { id: 'intel', node: <IntelPacketPanel token={token} subjectId={subjectId} subject={subject} /> },
                        { id: 'hostile', node: (
                            <div ref={hostilePanelRef}>
                                <HostileMentionPanel token={token} subjectId={subjectId} onIncidentsChanged={reload}
                                    selectedAccount={selectedAccount} onClearAccount={() => setSelectedAccount(null)} />
                            </div>
                        ) },
                        { id: 'breach', node: <BreachExposurePanel token={token} subjectId={subjectId} onIncidentsChanged={reload} openDialog={openDialog} /> },
                        { id: 'pipl', node: <PiplLookupPanel token={token} subjectId={subjectId} /> },
                        { id: 'osint', node: <OsintLookupPanel token={token} subjectId={subjectId} /> },
                        { id: 'companies', node: <CompaniesHousePanel token={token} subjectId={subjectId} subject={subject} /> },
                        { id: 'incidents', node: (
                            <div className="panel mb-6">
                                <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer"
                                    onClick={() => setIncidentsOpen(!incidentsOpen)}>
                                    <div className="font-display text-xl gold">Incidents ({incidents.total})</div>
                                    <div className="flex items-center gap-3">
                                        <div className="text-xs text-muted font-mono">grouped by classification</div>
                                        <div className="text-xs text-muted">{incidentsOpen ? '▾' : '▸'}</div>
                                    </div>
                                </div>
                                {incidentsOpen && (incidents.items.length === 0 ? (
                                    <div className="px-6 py-8 text-muted text-sm">No incidents logged against this subject.</div>
                                ) : (
                                    <IncidentGroups incidents={incidents.items} token={token} openDialog={openDialog} reload={reload} />
                                ))}
                            </div>
                        ) },
                        (subject.aliases.length > 0 || subject.handles.length > 0) ? { id: 'aliases_handles', node: (
                            <div className="grid grid-cols-2 gap-6 mb-6">
                                {subject.aliases.length > 0 && (
                                    <div className="panel">
                                        <div className="panel-header"><div className="font-display text-xl gold">Aliases<HelpTip text={PANEL_HELP.aliases} /></div></div>
                                        {subject.aliases.map(a => (
                                            <div key={a.id} className="table-row px-6 py-3 flex justify-between">
                                                <div>{a.alias}</div>
                                                {a.alias_type && <span className="text-xs text-muted font-mono">{a.alias_type}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {subject.handles.length > 0 && (
                                    <div className="panel">
                                        <div className="panel-header"><div className="font-display text-xl gold">Online Handles<HelpTip text={PANEL_HELP.online_handles} /></div></div>
                                        {subject.handles.map(h => (
                                            <div key={h.id} className="table-row px-6 py-3 flex justify-between">
                                                <div className="font-mono text-sm">@{h.handle}</div>
                                                <span className="text-xs text-muted font-mono">{h.platform}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) } : null,
                        { id: 'linked_businesses', node: <LinkedBusinessesPanel token={token} subjectId={subjectId} setView={setView} setSelectedBusiness={setSelectedBusiness} /> },
                    ]} />
                    </>
                    )}
                </div>
            );
        }

export { SubjectDetailView };
